import { yupResolver } from '@hookform/resolvers/yup';
import { FormActions } from '@openx/components-core/lib/FormActions/FormActions.tsx';
import { FormContainer } from '@openx/components-core/lib/FormContainer/FormContainer.tsx';
import { scrollToError } from '@openx/components-core/utils/scrollToError.ts';
import { difference } from '@openx/components-form/difference.ts';
import { PageHeader } from '@openx/components-header/PageHeader.tsx';
import type { ProviderData } from '@openx/types/provider.ts';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { ProviderFormState } from '../types.ts';
import { useConfirmationModal } from './hooks/useConfirmationModal.tsx';
import { AllowedOrganizationsSection } from './sections/AllowedOrganizationsSection.tsx';
import { ProviderDetailsSections } from './sections/ProviderDetailsSections.tsx';
import { filterAllowedAccounts, transformDataToMatchProviderForm } from './utils.ts';
import { getValidationSchema } from './validationSchema.ts';

type ProviderEditFormProps = {
  currentProvider: ProviderData;
};

export const ProviderEditForm = ({ currentProvider }: ProviderEditFormProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const handleEditProvider = useConfirmationModal();
  const initialValues = transformDataToMatchProviderForm(currentProvider);

  const formMethods = useForm<ProviderFormState>({
    defaultValues: initialValues,
    mode: 'all',
    resolver: yupResolver(getValidationSchema(t)),
  });

  const { isDirty, isSubmitting } = formMethods.formState;

  const onSubmit = (formValues: ProviderFormState) => {
    const formTouchedFields = Object.keys(difference(formValues, initialValues));

    const updatedAllowedAccounts = filterAllowedAccounts(
      currentProvider.id,
      initialValues.allowed_accounts,
      formValues.allowed_accounts
    );

    handleEditProvider(initialValues.name, formValues, formTouchedFields, updatedAllowedAccounts);
  };

  return (
    <PageHeader title={t('Edit: {provider}', { provider: currentProvider.name })} titlePrefix="OpenXSelect">
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(onSubmit, scrollToError)}>
          <FormContainer fullPage>
            <ProviderDetailsSections />
            <AllowedOrganizationsSection />

            <FormActions
              submitButtonLabel={t('Save')}
              cancelButtonLabel={t('Cancel')}
              onCancel={() => navigate('/providers')}
              isSubmitting={isSubmitting}
              disabled={!isDirty || isSubmitting}
            />
          </FormContainer>
        </form>
      </FormProvider>
    </PageHeader>
  );
};
